'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { type OptimizelyUserContext, createInstance, type default as optimizelySdk } from '@optimizely/optimizely-sdk';
import type CustomWindow from 'interfaces/Utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import { useReportWebVitals } from 'next/web-vitals';
import { OptimizelyContext } from '@/main/lib/hooks/useOptimizely';
import { getUserContext, invalidateCachedUser } from '@/app_lib/optimizely/utils';

declare let window: CustomWindow;

const isProduction = process.env.ENVIRONMENT?.toLowerCase() === 'prod';

const noOpLogger = {
  log: () => {},
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
};

const optimizely = createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_KEY,
  logger: isProduction ? noOpLogger : undefined,
});

export interface VariantProps {
  variant?: string | null;
  enabled?: boolean;
  variables?: any;
}

const OPTIMIZELY_FLAGS = [
  'readingProgressBarVariant',
  'hideHamburgerMenu_desktop',
  'readTime',
  'hideTopicMenu_mobile',
  'desktop_right_rail',
  'add_us_on_google_banner',
  'recurlyEngageModalTrigger',
  'bodyUnderlineLinks',
  'article_font_sizes',
] as const;
export type OptimizelyFlags = (typeof OPTIMIZELY_FLAGS)[number];

export interface OptimizelyUserInfo {
  user?: OptimizelyUserContext;
  // TODO: remove string and update the OptimizelyFlags
  [key: string]: VariantProps | OptimizelyUserContext | undefined;
}

// current year + 2 prior years, for matching article paths like /2026/01/...
const getRecentYears = (): string[] => {
  const currentYear = new Date().getFullYear();
  return [String(currentYear), String(currentYear - 1), String(currentYear - 2)];
};

const RECENT_YEARS = '__recent_years__';

const currentTests = [
  {
    location: [RECENT_YEARS, 'article'],
    tags: [],
    excludedTags: [],
    section: [],
    excludedSections: [],
    testName: 'socialShareV2',
  },
  { location: ['section'], testName: 'section' },
  { location: ['checkout'], testName: 'helio-setup' },
  { location: ['education'], testName: 'education' },
  { location: ['article'], testName: 'dateless-article' },
  { location: [RECENT_YEARS, 'article'], testName: 'articleStickyFooter' },
  {
    location: ['reviews-1', 'reviews-2', 'reviews-1/top-5-companies/', 'reviews-2/top-5-companies/'],
    testName: 'ndrTracking',
  },
  { location: ['people'], testName: 'peoplePage' },
  { location: [RECENT_YEARS, '2022', 'article'], testName: 'shrink_spacing_articles' },
  { location: [RECENT_YEARS, 'article'], testName: 'desktop_right_rail' },
  {
    location: ['article', RECENT_YEARS],
    primarySection: [
      'leadership',
      'future-of-work',
      'workplace-culture',
      'c-suite',
      'success',
      'politics',
      'innovation',
    ],
    excludedTags: ['always free', 'health evergreen', 'personal finance evergreen'],
    tags: ['ceo daily', 'chief executive office (ceo)', 'executives', 'federal reserve', 'tariffs and trade'],
    testName: 'pianoTemplateLocation',
    excludedPremiumCategory: 'f-partnership',
    evaluateTagsAndSectionsSeparately: true,
  },
  { location: ['*'], testName: 'hideHamburgerMenu_desktop' },
  { location: ['*'], testName: 'hideTopicMenu_mobile' },
  { location: ['section'], testName: 'replatform-section-redirect' },
  { location: ['tag'], testName: 'replatform-tag-redirect' },
  { location: [RECENT_YEARS, 'article', 'next'], testName: 'readTime' },
  { location: [RECENT_YEARS, 'article'], testName: 'readingProgressBarVariant' },
  { location: [RECENT_YEARS, 'article'], testName: 'nativeshare' },
  { location: [RECENT_YEARS, 'article', 'education'], testName: 'add_us_on_google_banner' },
  // TODO: Because of client side navigation stopping the Optimizely Provider from re-running, we will let any account page run the test
  // TODO: This ensures that once in the /account eco-system, the test will have run, so if they navigate internally or go directly to it, the test will have run.
  // TODO: Long term, we should update the Optimizely Provider to re-run when pathname changes, but for this test this works
  { location: ['account/'], testName: 'recurlyEngageModalTrigger' },
  { location: [RECENT_YEARS, 'article'], testName: 'bodyUnderlineLinks' },
  { location: [RECENT_YEARS, 'article'], testName: 'article_font_sizes' },
];

interface TestConfig {
  location: string[];
  primarySection?: string[];
  tags?: string[];
  excludedTags?: string[];
  excludedSections?: string[];
  section?: string[];
  excludedPremiumCategory?: string;
  evaluateTagsAndSectionsSeparately?: boolean;
  [key: string]: any;
}

const canRunTest = (test: TestConfig, pageAdConfig?: PageAdConfig) => {
  try {
    // Test rules
    const {
      location,
      primarySection,
      tags,
      excludedTags,
      excludedSections,
      excludedPremiumCategory,
      section,
      evaluateTagsAndSectionsSeparately,
    } = test;

    // Page ad config values & location to compare against
    // Primary Section or Page Channel
    const ch = pageAdConfig?.ch?.toLowerCase?.();
    const pageAdConfigTags = (pageAdConfig?.tags || []).map((tag: string) => tag.toLowerCase());
    const pageAdConfigPremiumCategory = pageAdConfig?.premiumCategory?.toLowerCase() || null;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const effectiveLocation = location.flatMap((loc) => (loc === RECENT_YEARS ? getRecentYears() : [loc]));
    const locationInPath = effectiveLocation.some((loc) => path.includes(loc)) || location[0] === '*';

    // Default if no sections are given as primary or general sections
    let sectionMatches = true;
    if (section && section.length > 0) {
      sectionMatches = section.some((sec) => ch === sec);
    } else if (primarySection && primarySection.length > 0) {
      sectionMatches = primarySection.some((sec) => ch === sec) || primarySection[0] === '*';
    }

    // Check tags from test rule against pageAdConfig tags
    const tagsInPageAdConfig = tags ? tags.some((tag) => pageAdConfigTags.includes(tag)) || tags[0] === '*' : true;

    // Check exclusions for sections, tags, and premium category
    const excludedSectionsNotInPageAdConfig = excludedSections ? !excludedSections.some((sec) => ch === sec) : true;
    const excludedTagsNotInPageAdConfig = excludedTags
      ? !excludedTags.some((tag) => pageAdConfigTags.includes(tag))
      : true;
    const excludedPremiumCategoryNotInPageAdConfig = excludedPremiumCategory
      ? excludedPremiumCategory !== pageAdConfigPremiumCategory
      : true;

    const validTagsAndSections = evaluateTagsAndSectionsSeparately
      ? sectionMatches || tagsInPageAdConfig
      : sectionMatches && tagsInPageAdConfig;

    const hasNoExclusions =
      excludedTagsNotInPageAdConfig && excludedSectionsNotInPageAdConfig && excludedPremiumCategoryNotInPageAdConfig;

    return locationInPath && validTagsAndSections && hasNoExclusions;
  } catch {
    return false;
  }
};

export type OptimizelySdk = typeof optimizelySdk;

export const OptimizelyProvider = ({
  children,
  pageAdConfig,
}: {
  children: React.ReactNode;
  pageAdConfig?: PageAdConfig;
}) => {
  const [userFlags, setUserFlags] = useState<OptimizelyUserInfo>({});

  // persist scroll depth between renders
  const scrollDepthRef = useRef<number>(0);

  const reportWebVitalsCallback = useCallback(
    async (metric: any) => {
      try {
        const user = await getUserContext(optimizely, pageAdConfig);
        if (user) {
          const properties = {
            metric_name: metric.name, // e.g. FCP, LCP, CLS, TTFB , FID, INP.
            metric_id: metric.id,
          };
          const tags = {
            value: metric.value,
            $opt_event_properties: properties,
          };
          // @ts-ignore
          user.trackEvent(metric.name, tags);
        }
      } catch {}
    },
    [pageAdConfig],
  );

  useReportWebVitals(reportWebVitalsCallback);

  // stable scroll listener
  const scrollListener = useCallback(async () => {
    try {
      const user = await getUserContext(optimizely, pageAdConfig);
      const scrolled = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight || 1;
      const depth = Math.round((scrolled / totalHeight) * 100);
      if (depth > scrollDepthRef.current + 5) {
        const depthToAdd = depth - scrollDepthRef.current;
        scrollDepthRef.current = depth;
        if (user) {
          // @ts-ignore
          user.trackEvent('scroll_depth', { value: depthToAdd });
          // add scroll depth to session storage
          try {
            const currentScrollDepth = sessionStorage.getItem('scroll_depth');
            const newScrollDepth = currentScrollDepth ? Number(currentScrollDepth) + depthToAdd : depthToAdd;
            sessionStorage.setItem('scroll_depth', newScrollDepth.toString());
          } catch {}
        }
      }
    } catch {}
  }, [pageAdConfig]);

  useEffect(() => {
    // Invalidate module-level cache when pageAdConfig changes to ensure attributes/user are correct
    invalidateCachedUser();

    // expose currentTests for visibility handler fallback
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).__optimizely_current_tests = currentTests;

    // remove any old scroll listener and add a stable one
    try {
      window.removeEventListener('scroll', scrollListener);
    } catch {}

    const beforeUnloadHandler = async () => {
      try {
        const user = await getUserContext(optimizely, pageAdConfig);
        if (user) {
          user.trackEvent('final_timer', { value: 1 });
        }
      } catch {}
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    const calculateScrollDepth = () => {
      window.addEventListener('scroll', scrollListener, { passive: true });
    };

    void optimizely?.onReady().then(async ({ success }) => {
      if (!success || !optimizely) {
        return;
      }

      // ensure module-level cache is primed
      const user = await getUserContext(optimizely, pageAdConfig);

      if (!user) return;

      const newUserFlags: OptimizelyUserInfo = { user };
      const urlParams = new URLSearchParams(window.location.search);

      for (let i = 0; i !== currentTests.length; i += 1) {
        const { testName } = currentTests[i];
        const testOverride = urlParams.get(testName);
        const hasUrlOverride = testOverride !== null;

        const canRun = hasUrlOverride || canRunTest(currentTests[i], pageAdConfig);

        if (!canRun) {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
          continue;
        }

        try {
          const decision = user.decide(testName);
          const testOverrideBoolean = testOverride ? testOverride.toLowerCase() !== 'off' : decision.enabled;
          const testOverrideVariant = testOverride ? testOverride.toLowerCase() : decision.variationKey;

          newUserFlags[testName] = {
            enabled: testOverrideBoolean,
            variables: decision.variables,
            variant: testOverrideVariant,
          };
        } catch {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
        }
      }

      // Send active Optimizely tests to DebugBear RUM for filtering
      const activeTests: string[] = [];
      for (const testName in newUserFlags) {
        if (testName === 'user') continue;
        const flag = newUserFlags[testName] as VariantProps | undefined;
        if (flag?.enabled && flag?.variant) {
          activeTests.push(`${testName}:${flag.variant}`);
        }
      }
      if (activeTests.length > 0) {
        window.dbbRum = window.dbbRum || [];
        window.dbbRum.push(['tag1', activeTests.join('|')]);
      }

      setUserFlags(newUserFlags);
      user.trackEvent('pages_visited', { value: 1 });

      // Track return visits via UTM native share
      const utmSource = urlParams.get('utm_source');
      if (utmSource === 'native_share') {
        user.trackEvent('return_visits_via_utm', { value: 1 });
      }
    });

    calculateScrollDepth();

    return () => {
      try {
        window.removeEventListener('scroll', scrollListener);
      } catch {}
      try {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      } catch {}
    };
  }, [pageAdConfig, scrollListener]);
  // @ts-ignore
  return <OptimizelyContext.Provider value={{ userFlags, optimizely }}>{children}</OptimizelyContext.Provider>;
};

export const OptimizelyScript: React.FC = () => null;
